import { useEffect, useState } from 'react';

/**
 * An **asymmetric debounce** on a boolean — trailing on the `true` edge, leading
 * (immediate) on the `false` edge:
 *
 * - rising edge (→ `true`) is **delayed**: returns `true` only once `value` has
 *   been continuously `true` for at least `delayMs`;
 * - falling edge (→ `false`) is **immediate**: returns `false` the instant
 *   `value` is `false`.
 *
 * This is deliberately NOT a symmetric `useDebounce(value, delayMs)` — a symmetric
 * debounce would also hold the `true → false` transition for `delayMs`, which for
 * a "should I show the empty state?" flag means keeping the empty state up for a
 * beat *after* real data has arrived. Do not swap one in.
 *
 * Use it to avoid acting on a `true` that might be a transient blip — e.g. two
 * independent reactive sources that briefly disagree for a frame. If `value`
 * flips back to `false` within the window, the pending timer is cleared and the
 * hook never reports `true`.
 */
export function useDelayedTrue(value: boolean, delayMs: number): boolean {
  const [elapsed, setElapsed] = useState(false);

  useEffect(() => {
    if (!value) return;
    const id = setTimeout(() => setElapsed(true), delayMs);
    return () => {
      clearTimeout(id);
      // Re-arm the delay for the next rising edge (the return value already
      // reads `false` via the `value &&` guard below the moment `value` drops).
      setElapsed(false);
    };
  }, [value, delayMs]);

  return value && elapsed;
}
